import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  businessService,
  Business,
  BusinessFormData,
} from '../services/businessService';
import { BusinessUser } from '../types/business';
import { queryKeys } from '../config/queryKeys';

interface UseBusinessUsersParams {
  businessId?: string;
  enabled?: boolean;
}

export function useBusinesses(enabled = true) {
  return useQuery({
    queryKey: queryKeys.businesses.list(),
    queryFn: () => businessService.getBusinesses(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - businesses rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useBusiness(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.businesses.detail(id!),
    queryFn: () => businessService.getBusiness(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useBusinessUsers(params: UseBusinessUsersParams = {}) {
  const { businessId, enabled = true } = params;

  return useQuery({
    queryKey: queryKeys.businesses.users(businessId || 'default'),
    queryFn: () => businessService.getBusinessUsers(businessId),
    enabled: enabled && !!businessId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateBusiness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, business }: { id: string; business: BusinessFormData }) =>
      businessService.updateBusiness(id, business),
    onSuccess: (updatedBusiness) => {
      // Update the specific business in cache
      queryClient.setQueryData(
        queryKeys.businesses.detail(updatedBusiness.id),
        updatedBusiness
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.businesses.list() });
    },
  });
}
